import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  Box, Typography, IconButton, TextField,
  Paper, Divider, Chip, Fade, Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon       from '@mui/icons-material/Close';
import SendIcon        from '@mui/icons-material/Send';
import SmartToyIcon    from '@mui/icons-material/SmartToy';

const NAVY = '#0A2342';
const TEAL = '#00695C';
const TEAL_LIGHT = '#E0F2F1';

const QUICK_CHIPS = [
  'Check my CE hours',
  'When does my licence expire?',
  'What do I need to renew?',
];

function TypingDots() {
  return (
    <Box display="flex" alignItems="center" gap={0.5} px={1.5} py={1}>
      {[0, 1, 2].map(i => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: '#94a3b8',
            animation: 'pulse 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
              '50%':       { opacity: 1,   transform: 'scale(1)' },
            },
          }}
        />
      ))}
    </Box>
  );
}

function Message({ msg }) {
  const isAI = msg.role === 'assistant';
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isAI ? 'flex-start' : 'flex-end',
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          maxWidth: '88%',
          px: 1.5,
          py: 1,
          borderRadius: isAI ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
          bgcolor: isAI ? '#f1f5f9' : TEAL,
          color: isAI ? '#1e293b' : '#fff',
          fontSize: 13,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          boxShadow: isAI ? 'none' : '0 1px 4px rgba(0,137,123,0.25)',
        }}
      >
        {msg.text}
      </Box>
    </Box>
  );
}

export default function AIAssistantWidget() {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [greeted, setGreeted]   = useState(false);
  const bottomRef               = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // Fetch broker context and send greeting on first open
  const initChat = useCallback(async () => {
    if (greeted) return;
    setGreeted(true);
    setLoading(true);
    try {
      const { data } = await api.get('/ai/broker-context');
      const { broker, ce } = data;
      const ceText = ce.hours_remaining > 0
        ? `You have ${ce.hours_completed.toFixed(1)} of ${ce.hours_required.toFixed(1)} CE hours completed — ${ce.hours_remaining.toFixed(1)} still needed`
        : `Your CE is up to date (${ce.hours_completed.toFixed(1)} hours completed)`;

      const greeting = `Hi ${broker.name.split(' ')[0] || 'there'} — you're licensed in ${broker.province || 'your province'} (${broker.licence_class || 'your class'}${broker.licence_number ? `, #${broker.licence_number}` : ''}) under ${broker.firm_name || 'your firm'}. ${ceText}. What can I help you with today?`;

      setMessages([{ role: 'assistant', text: greeting }]);
    } catch {
      setMessages([{ role: 'assistant', text: "Hi! I'm Authra Advisor, your AI-powered compliance assistant. I can help with CE hours, licence renewals, and compliance questions. What can I help you with?" }]);
    } finally {
      setLoading(false);
    }
  }, [greeted]);

  const handleOpen = () => {
    setOpen(true);
    initChat();
  };

  const sendMessage = useCallback(async (text) => {
    const question = (text || input).trim();
    if (!question || loading) return;
    setInput('');

    const userMsg = { role: 'user', text: question };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    // Build history from all messages except the last user message we just added
    const history = messages.filter(m => m.role === 'assistant' || m.role === 'user');

    try {
      const { data } = await api.post('/ai/broker-chat', {
        message: question,
        history,
      });
      setMessages(prev => [...prev, { role: 'assistant', text: data.response || 'No response received.' }]);
    } catch (err) {
      const msg = err.response?.data?.error || 'Sorry, the AI service is temporarily unavailable. Please try again in a moment.';
      setMessages(prev => [...prev, { role: 'assistant', text: msg }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      {/* Floating bubble */}
      <Tooltip title="Authra Advisor" placement="left">
        <Box
          onClick={open ? () => setOpen(false) : handleOpen}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 54,
            height: 54,
            borderRadius: '50%',
            bgcolor: NAVY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(10,35,66,0.4)',
            zIndex: 1400,
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'scale(1.08)',
              boxShadow: '0 6px 28px rgba(10,35,66,0.55)',
            },
          }}
        >
          {open
            ? <CloseIcon sx={{ color: '#fff', fontSize: 22 }} />
            : (
              <>
                <SmartToyIcon sx={{ color: '#fff', fontSize: 24 }} />
                <Box
                  sx={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    bgcolor: TEAL,
                    color: '#fff',
                    fontSize: 9,
                    fontWeight: 700,
                    px: 0.6,
                    py: 0.15,
                    borderRadius: 0.75,
                    lineHeight: 1.4,
                    letterSpacing: '0.04em',
                    animation: 'subtlePulse 2.5s ease-in-out infinite',
                    '@keyframes subtlePulse': {
                      '0%, 100%': { boxShadow: '0 0 0 0 rgba(0,137,123,0.5)' },
                      '50%':       { boxShadow: '0 0 0 5px rgba(0,137,123,0)' },
                    },
                  }}
                >
                  AI
                </Box>
              </>
            )
          }
        </Box>
      </Tooltip>

      {/* Chat panel */}
      <Fade in={open}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 90,
            right: 24,
            width: 380,
            maxWidth: 'calc(100vw - 48px)',
            height: 520,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1399,
            border: `1px solid ${TEAL}40`,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: NAVY,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <AutoAwesomeIcon sx={{ color: TEAL, fontSize: 18 }} />
            <Box flex={1}>
              <Typography variant="body2" fontWeight={700} color="#fff" lineHeight={1.2}>
                Authra Advisor
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 10 }}>
                Powered by Claude · Your compliance data only
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{ color: 'rgba(255,255,255,0.65)', p: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 1.5,
              py: 1.5,
              bgcolor: '#f8fafc',
            }}
          >
            {messages.map((m, i) => <Message key={i} msg={m} />)}
            {loading && (
              <Box display="flex" alignItems="center">
                <Box sx={{ bgcolor: '#f1f5f9', borderRadius: '4px 12px 12px 12px' }}>
                  <TypingDots />
                </Box>
              </Box>
            )}
            <div ref={bottomRef} />
          </Box>

          {/* Quick action chips */}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderTop: '1px solid #e2e8f0',
              bgcolor: '#fff',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {QUICK_CHIPS.map(q => (
                <Chip
                  key={q}
                  label={q}
                  size="small"
                  onClick={() => sendMessage(q)}
                  disabled={loading}
                  sx={{
                    fontSize: 10,
                    height: 22,
                    cursor: 'pointer',
                    bgcolor: TEAL_LIGHT,
                    color: '#00695C',
                    fontWeight: 500,
                    '&:hover': { bgcolor: '#B2DFDB' },
                    '&.Mui-disabled': { opacity: 0.5 },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Input */}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              bgcolor: '#fff',
              flexShrink: 0,
            }}
          >
            <TextField
              multiline
              maxRows={3}
              fullWidth
              size="small"
              placeholder="Ask Authra Advisor about CE, licences, renewals…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: 13,
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: TEAL,
                  },
                },
              }}
            />
            <IconButton
              size="small"
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              sx={{
                bgcolor: TEAL,
                color: '#fff',
                borderRadius: 1.5,
                p: 1,
                flexShrink: 0,
                '&:hover': { bgcolor: '#00796B' },
                '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
              }}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: '#5B6B7F',
              fontSize: '10px',
              px: 1,
              pb: 0.75,
              bgcolor: '#fff',
              lineHeight: 1.3,
            }}
          >
            General compliance information only. Not legal advice. Scoped to your account.
          </Typography>
        </Paper>
      </Fade>
    </>
  );
}
